import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button } from '../../components/Button';
import { TextInput } from '../../components/TextInput';
import { TermsAgreement } from '../legal/TermsAgreement';
import {
  createSignupAccount,
  isCurrentUserEmailVerified,
  sendSignupVerificationEmail,
  signInForSignup,
} from '../../services/auth';
import { plansForSport } from '../../services/planTierOffers';
import { sportPriceCents } from '../../services/signup';
import type { SignupLookupResponse, SignupSport } from '../../types/signup';
import { SignupHeader } from './SignupLayout';
import { SignupSchoolSummary } from './SignupSchoolSummary';
import { SignupSportChoice } from './SignupSportChoice';
import { SignupPlanChoice } from './SignupPlanChoice';
import { formatAnnualPrice, formatPrice } from './signupFormat';
import { SignupPriceConfirmation } from './SignupPriceConfirmation';

/** Mirrors AcceptInviteForm and the API's own name rule (100 characters). */
const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 128;
const MAX_NAME_LENGTH = 100;
/** The API's limit on a typed school name (`SelfServeSignup.MaxSchoolNameLength`). */
const MAX_SCHOOL_NAME_LENGTH = 100;
/**
 * The API's rule for a typed school name (`SignupSchoolNameFor`): fold it to plain Latin (accents and
 * compatibility forms such as full-width letters removed, curly quotes made straight), then allow only
 * English letters, digits, spaces and . , ' & - ( ) / :. Lookalike letters from another script, emoji
 * and hidden characters (zero-width, bidi, control) are refused. Checked here too so the coach hears
 * it before the account is created, not after verifying.
 */
const ALLOWED_SCHOOL_NAME = /^[A-Za-z0-9 .,'&\-()/:]*$/;
const toPlainLatin = (value: string): string =>
  value
    .normalize('NFKC')
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/[\u2018\u2019\u02BC]/g, "'")
    .replace(/[\u2010-\u2014]/g, '-');
const isPlainSchoolName = (value: string): boolean => ALLOWED_SCHOOL_NAME.test(toPlainLatin(value.trim()));

type AccountFormData = {
  /** Only asked when the lookup did not match the address to a school (`schoolMatched: false`). */
  schoolName: string;
  firstName: string;
  lastName: string;
  sport: SignupSport | undefined;
  /** The plan key, only read when the chosen sport is priced by plan. */
  planTier: string | undefined;
  password: string;
  confirmPassword: string;
};

export interface SignupAccountCreated {
  firstName: string;
  lastName: string;
  sport: SignupSport;
  /** The plan key the coach chose, when their sport is priced by plan. */
  planTier?: string;
  /** The school name the coach typed, only when the lookup did not match their school. */
  schoolName?: string;
  /** The coach agreed to the Terms of Service and Privacy Policy; the button is disabled until they do. */
  acceptedTerms: true;
  /** The Firebase account was already verified (a resumed signup), so there is nothing to wait for. */
  alreadyVerified: boolean;
  /** The verification email could not be sent; the verify step offers Resend straight away. */
  verificationSendFailed: boolean;
}

/** What the form starts with when a coach signs back in to a signup already under way. */
export interface SignupAccountInitialValues {
  firstName: string;
  lastName: string;
  sport: SignupSport;
  planTier?: string;
  schoolName?: string;
  /** The server refused the typed school name: its message, shown on the School name field. */
  schoolNameError?: string;
}

/**
 * The plan to send for `sport`: the chosen one when that sport still offers it. Undefined when the
 * sport is not priced by plan (nothing is sent), or when nothing valid was chosen, which the form's
 * own rule refuses before submit. No plan is picked for the coach: there is no clean way to tell
 * which one fits a program, so the coach chooses.
 */
const planKeyFor = (
  lookup: Pick<SignupLookupResponse, 'planTiers'>,
  sport: SignupSport | undefined,
  chosen: string | undefined
): string | undefined =>
  plansForSport(lookup.planTiers, sport).find((plan) => plan.key === chosen)?.key;

const firebaseCode = (error: unknown): string | undefined =>
  typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code: unknown }).code)
    : undefined;

const createAccountMessage = (code: string | undefined): string => {
  switch (code) {
    case 'auth/weak-password':
      return 'Choose a stronger password.';
    case 'auth/invalid-email':
      return 'That email address is not valid.';
    case 'auth/too-many-requests':
      return 'Too many attempts, try again later.';
    case 'auth/network-request-failed':
      return 'We could not reach RosterPilot. Check your connection and try again.';
    default:
      return 'We could not create your account. Please try again.';
  }
};

/**
 * Second step, for an eligible school only: the coach's name, a password, the sport, and a plain
 * statement of what the program will be billed after its free trial. Creates the Firebase account and sends the
 * verification email; the RosterPilot program is not created until the address is verified.
 */
export const SignupAccountStep = ({
  email,
  lookup,
  initialValues,
  onCreated,
  onEmailInUse,
  onChangeEmail,
}: {
  email: string;
  lookup: SignupLookupResponse;
  initialValues?: SignupAccountInitialValues;
  onCreated: (created: SignupAccountCreated) => void;
  onEmailInUse: () => void;
  onChangeEmail: () => void;
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // Not remembered across a "sign in again" round trip on purpose: the coach agrees on this screen.
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const sports = lookup.sports;
  // The address matched no school in our list: the coach names it, and only plan-priced sports are offered.
  const asksSchoolName = !lookup.schoolMatched;
  const initialSport =
    initialValues && sports.includes(initialValues.sport) ? initialValues.sport : undefined;

  const {
    register,
    control,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<AccountFormData>({
    mode: 'onSubmit',
    defaultValues: {
      schoolName: initialValues?.schoolName ?? '',
      firstName: initialValues?.firstName ?? '',
      lastName: initialValues?.lastName ?? '',
      // One sport is the only possible answer, so it is chosen for them.
      sport: initialSport ?? (sports.length === 1 ? sports[0] : undefined),
      // Only a plan the coach already chose (signing back in); otherwise the coach picks one.
      planTier: initialSport ? planKeyFor(lookup, initialSport, initialValues?.planTier) : undefined,
      password: '',
      confirmPassword: '',
    },
  });

  // The server's refusal of the typed name, put on the field once the form is up.
  const serverSchoolNameError = initialValues?.schoolNameError;
  useEffect(() => {
    if (serverSchoolNameError) {
      setError('schoolName', { type: 'server', message: serverSchoolNameError }, { shouldFocus: true });
    }
  }, [serverSchoolNameError, setError]);

  const password = watch('password');
  const chosenSport = watch('sport');
  const chosenPlanKey = watch('planTier');
  const plans = plansForSport(lookup.planTiers, chosenSport);
  const chosenPlan = plans.find((plan) => plan.key === chosenPlanKey);
  // Sports priced by plan quote a "from" price (their cheapest plan) until a plan is chosen.
  const planPricedSports = sports.filter((sport) => plansForSport(lookup.planTiers, sport).length > 0);

  // Prices can differ by sport. When they do, each radio carries its own price; when every sport
  // costs the same, the one price is quoted before a sport is chosen. The confirmation always
  // quotes the chosen sport's price.
  const knownPrices = sports
    .map((sport) => sportPriceCents(lookup, sport))
    .filter((cents): cents is number => cents !== null);
  const onePrice =
    planPricedSports.length === 0 &&
    knownPrices.length > 0 &&
    knownPrices.every((cents) => cents === knownPrices[0])
      ? knownPrices[0]
      : null;
  const priceLabels: Partial<Record<SignupSport, string>> = {};
  if (onePrice === null) {
    for (const sport of sports) {
      const cents = sportPriceCents(lookup, sport);
      if (cents === null) continue;
      const label = formatAnnualPrice(cents, lookup.currency);
      priceLabels[sport] = planPricedSports.includes(sport) ? `From ${label}` : label;
    }
  }
  // A plan-priced sport quotes nothing until a plan is picked: its "from" price is not what it bills.
  const priceCents = chosenPlan
    ? chosenPlan.annualPriceCents
    : plans.length > 0
      ? null
      : chosenSport
        ? sportPriceCents(lookup, chosenSport)
        : onePrice;
  const price = priceCents !== null ? formatPrice(priceCents, lookup.currency) : null;

  const onSubmit = async (data: AccountFormData) => {
    // `required` on the Controller guarantees this; the guard keeps it a checked fact.
    if (!data.sport) return;
    // The button is disabled until the box is checked; Enter in a field must not get round it.
    if (!acceptedTerms) return;

    setSubmitError('');
    setSubmitting(true);

    try {
      try {
        await createSignupAccount(email, data.password);
      } catch (error: unknown) {
        if (firebaseCode(error) !== 'auth/email-already-in-use') {
          setSubmitError(createAccountMessage(firebaseCode(error)));
          return;
        }

        // The address already has a Firebase account. When the password matches, it is this coach
        // coming back to a signup they started earlier, so pick it up rather than strand them: an
        // account with no RosterPilot program cannot sign in anywhere else. Otherwise it belongs to
        // someone who should sign in instead.
        try {
          await signInForSignup(email, data.password);
        } catch {
          onEmailInUse();
          return;
        }
      }

      const alreadyVerified = isCurrentUserEmailVerified();
      let verificationSendFailed = false;

      if (!alreadyVerified) {
        try {
          await sendSignupVerificationEmail();
        } catch {
          verificationSendFailed = true;
        }
      }

      const planTier = planKeyFor(lookup, data.sport, data.planTier);
      onCreated({
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        sport: data.sport,
        ...(planTier ? { planTier } : {}),
        ...(asksSchoolName && data.schoolName.trim() ? { schoolName: data.schoolName.trim() } : {}),
        acceptedTerms: true,
        alreadyVerified,
        verificationSendFailed,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SignupHeader title="Create your account" subtitle={email} />

      {asksSchoolName && (
        <div className="accept-invite-message signup-message--info" data-testid="signup-school-not-matched">
          <p>
            We don&apos;t have your school in our list yet. You can still sign up and start your free
            trial: tell us your school&apos;s name below.
          </p>
        </div>
      )}

      {lookup.school && (
        <SignupSchoolSummary
          school={lookup.school}
          annualPriceCents={lookup.annualPriceCents}
          sportPrices={lookup.sportPrices}
          fromSports={planPricedSports}
          currency={lookup.currency}
        />
      )}

      <form className="accept-invite-form" onSubmit={handleSubmit(onSubmit)}>
        {submitError && (
          <div className="accept-invite-message accept-invite-message--error" role="alert">
            <p>{submitError}</p>
          </div>
        )}

        <div className="accept-invite-inputs">
          {asksSchoolName && (
            <TextInput
              label="School name"
              autoComplete="organization"
              error={errors.schoolName?.message}
              disabled={submitting}
              data-testid="signup-school-name"
              {...register('schoolName', {
                validate: (value) =>
                  value.trim().length === 0
                    ? "Enter your school's name"
                    : isPlainSchoolName(value)
                      ? true
                      : 'Use English letters, numbers and basic punctuation only',
                maxLength: {
                  value: MAX_SCHOOL_NAME_LENGTH,
                  message: `School name must be ${MAX_SCHOOL_NAME_LENGTH} characters or fewer`,
                },
              })}
            />
          )}

          <Controller
            control={control}
            name="sport"
            rules={{ required: 'Choose your sport' }}
            render={({ field }) => (
              <SignupSportChoice
                sports={sports}
                priceLabels={priceLabels}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.sport?.message}
                disabled={submitting}
              />
            )}
          />

          {plans.length > 0 && (
            <Controller
              control={control}
              name="planTier"
              rules={{
                validate: (value, values) =>
                  planKeyFor(lookup, values.sport, value) !== undefined ||
                  plansForSport(lookup.planTiers, values.sport).length === 0 ||
                  'Choose a plan',
              }}
              render={({ field }) => (
                <SignupPlanChoice
                  plans={plans}
                  currency={lookup.currency}
                  threeYearOfferEndsOn={lookup.threeYearOfferEndsOn ?? null}
                  value={chosenPlan?.key}
                  onChange={field.onChange}
                  disabled={submitting}
                  error={errors.planTier?.message}
                />
              )}
            />
          )}

          <TextInput
            label="First Name"
            autoComplete="given-name"
            error={errors.firstName?.message}
            disabled={submitting}
            data-testid="signup-first-name"
            {...register('firstName', {
              validate: (value) => value.trim().length > 0 || 'Enter your first name',
              maxLength: {
                value: MAX_NAME_LENGTH,
                message: `First name must be ${MAX_NAME_LENGTH} characters or fewer`,
              },
            })}
          />

          <TextInput
            label="Last Name"
            autoComplete="family-name"
            error={errors.lastName?.message}
            disabled={submitting}
            data-testid="signup-last-name"
            {...register('lastName', {
              validate: (value) => value.trim().length > 0 || 'Enter your last name',
              maxLength: {
                value: MAX_NAME_LENGTH,
                message: `Last name must be ${MAX_NAME_LENGTH} characters or fewer`,
              },
            })}
          />

          <TextInput
            label="Password"
            type="password"
            autoComplete="new-password"
            error={errors.password?.message}
            disabled={submitting}
            data-testid="signup-password"
            {...register('password', {
              required: 'Choose a password',
              minLength: {
                value: MIN_PASSWORD_LENGTH,
                message: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
              },
              maxLength: {
                value: MAX_PASSWORD_LENGTH,
                message: `Password must be ${MAX_PASSWORD_LENGTH} characters or fewer`,
              },
            })}
          />

          <TextInput
            label="Confirm Password"
            type="password"
            autoComplete="new-password"
            error={errors.confirmPassword?.message}
            disabled={submitting}
            data-testid="signup-confirm-password"
            {...register('confirmPassword', {
              required: 'Confirm your password',
              validate: (value) => value === password || 'Passwords do not match',
            })}
          />
        </div>

        {price && <SignupPriceConfirmation price={price} trialDays={lookup.trialDays} />}

        <TermsAgreement
          checked={acceptedTerms}
          onChange={setAcceptedTerms}
          disabled={submitting}
          testId="signup-accept-terms"
        />

        <div className="accept-invite-actions">
          <Button
            type="submit"
            fullWidth
            loading={submitting}
            disabled={!acceptedTerms}
            data-testid="signup-create-account"
          >
            Create Account
          </Button>
          <button
            type="button"
            className="signup-text-button"
            onClick={onChangeEmail}
            disabled={submitting}
            data-testid="signup-change-email"
          >
            Use a different email
          </button>
        </div>
      </form>
    </>
  );
};
